import React, { useId } from 'react';
import { Switch } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { WidgetProps } from '@rjsf/utils';
import { routeDetailsClasses } from 'styles/tailwindClassnames';
import SwitchVisual, {
  switchTrackFill,
} from 'shared/components/inputs/SwitchVisual';

interface CustomCheckboxProps {
  schema?: any;
  value?: any;
  label: string;
  checked?: boolean;
  itemsData?: any;
  enumItem?: any;
  onChange: (updatedItems: boolean | any[]) => void;
  formData: any;
  disabled?: boolean;
  readonly?: boolean;
  options?: Record<string, any>;
  rawErrors?: string[];
}

const CustomCheckbox: React.FC<CustomCheckboxProps> = ({
  schema,
  value,
  label,
  checked,
  itemsData,
  enumItem,
  onChange,
  formData,
  disabled,
  readonly,
  options,
  rawErrors,
}) => {
  const { t } = useTranslation();
  // enumItem defined means we're rendering one item of an array-enum field.
  // formData may be undefined on the first render before CustomArrayField initialises it,
  // so fall back to itemsData (the already-selected items array) for the checked state.
  const isArrayItem = enumItem !== undefined && enumItem !== null;
  const selectedItems: any[] = Array.isArray(formData)
    ? formData
    : Array.isArray(itemsData)
      ? itemsData
      : [];
  const isChecked = isArrayItem
    ? selectedItems.includes(enumItem)
    : (formData ?? value ?? checked ?? false);

  const handleChange = (checked: boolean) => {
    onChange(checked);
  };

  const fieldLabel = schema?.title || label;
  // A field that failed validation gets a red ring, as text boxes get a red
  // border.
  const hasError = !!options?.hasError || !!rawErrors?.length;
  // Stable, HTML-valid id for aria-labelledby — labels (e.g. oneOf titles)
  // can contain spaces/punctuation and are not guaranteed unique.
  const labelId = useId();
  const switchEl = (
    <Switch
      checked={isChecked}
      onChange={handleChange}
      disabled={disabled || readonly}
      className={` ${routeDetailsClasses?.switchContainer || ''} ${switchTrackFill(isChecked)} disabled:cursor-not-allowed disabled:opacity-50 ${hasError ? 'ring-2 ring-ui-status-error-destructive ring-offset-1' : ''}`}
      aria-invalid={hasError || undefined}
      aria-label={isArrayItem ? undefined : fieldLabel}
      aria-labelledby={isArrayItem ? labelId : undefined}
    >
      <SwitchVisual on={isChecked} />
    </Switch>
  );
  // Every switch says what state it is in, as in the spec sheet. The word is
  // for sight only: the switch already announces its state as checked or not.
  const stateWord = (
    <span aria-hidden="true" className="text-body-sm text-ui-text-primary-text">
      {isChecked ? t('On') : t('Off')}
    </span>
  );

  return isArrayItem ? (
    <div className="flex w-full items-center justify-between">
      <span id={labelId} className="text-ui-text-primary-text">
        {fieldLabel}
      </span>
      <span className="flex items-center gap-2">
        {switchEl}
        {stateWord}
      </span>
    </div>
  ) : (
    <span className="inline-flex items-center gap-2">
      {switchEl}
      {stateWord}
    </span>
  );
};

// rjsf's CheckboxWidget, for booleans generateUISchema doesn't reach (fields
// inside array items). rjsf's own draws the description and label again beside
// the ones HorizontalFieldTemplate already shows; the switch draws neither.
export const CheckboxSwitchWidget = (props: WidgetProps) => (
  <CustomCheckbox {...props} formData={props.value} />
);

export default CustomCheckbox;
